import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

const durationLabel = (days, t) => {
  if (days === 30)  return t("/ Month");
  if (days === 60)  return t("/ 2 Months");
  if (days === 90)  return t("/ 3 Months");
  if (days === 180) return t("/ 6 Months");
  if (days === 365) return t("/ Year");
  return t("/ {days} Days", { days });
};

// ─── Style constants ─────────────────────────────────────────────────────────
// White pill contrasts on both the default dark card (#222) and the
// primary-coloured active/hover state.
const libTagBase = {
  display: "inline-block",
  padding: "4px 13px",
  borderRadius: 3,
  background: "rgba(255,255,255,0.92)",
  color: "#111",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.3,
  lineHeight: "1.4",
  border: "none",
  outline: "none",
  cursor: "pointer",
};

const libSection = {
  borderTop: "1px solid rgba(255,255,255,0.15)",
  borderBottom: "1px solid rgba(255,255,255,0.15)",
  paddingTop: 14,
  paddingBottom: 16,
  marginBottom: 6,
};

const libLabel = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 1.8,
  color: "rgba(255,255,255,0.45)",
  marginBottom: 10,
};

export const DynamicLibraryModal = ({
  pkg,
  selectedIds,
  onToggle,
  onClose,
  onConfirm,
  loading,
  error,
}) => {
  const { t } = useLanguage();
  if (!pkg) return null;

  const required = Number(pkg.library_selection_count || 0);
  const complete = selectedIds.length === required;

  return (
    <div className="hpa-dynamic-package-backdrop" role="dialog" aria-modal="true" aria-labelledby="dynamic-package-title">
      <div className="hpa-dynamic-package-dialog">
        <button type="button" className="hpa-dynamic-package-close" onClick={onClose} aria-label={t("Close")}>
          &times;
        </button>
        <span className="hpa-dynamic-package-kicker">{t("Build your access")}</span>
        <h3 id="dynamic-package-title">{pkg.name}</h3>
        <p>
          {t("Choose exactly {count} libraries. Your subscription will unlock only your selected libraries.", { count: required })}
        </p>

        <div className="hpa-dynamic-library-grid">
          {(pkg.libraries || []).map((library) => {
            const selected = selectedIds.includes(library.id);
            const disabled = !selected && selectedIds.length >= required;

            return (
              <button
                type="button"
                key={library.id}
                className={`hpa-dynamic-library-option ${selected ? "selected" : ""}`}
                onClick={() => onToggle(library.id)}
                disabled={disabled}
              >
                <span className="hpa-dynamic-library-check">
                  <i className={`fa-solid ${selected ? "fa-check" : "fa-plus"}`} />
                </span>
                <strong>{library.name}</strong>
              </button>
            );
          })}
        </div>

        <div className="hpa-dynamic-selection-count">
          {t("{selected} of {required} selected", { selected: selectedIds.length, required })}
        </div>
        {error && <div className="hpa-dynamic-package-error">{error}</div>}

        <button
          type="button"
          className="btn btn-primary btn-skew hpa-dynamic-package-confirm"
          onClick={onConfirm}
          disabled={!complete || loading}
        >
          <span>{loading ? t("Preparing payment…") : t("Continue to Payment")}</span>
        </button>
      </div>
    </div>
  );
};

// ─── PricingCard ─────────────────────────────────────────────────────────────

const PricingCard = ({ pkg, active, onMouseEnter, onGetAccess }) => {
  const { t } = useLanguage();
  const navigate  = useNavigate();
  const libraries = pkg.libraries || [];

  const fallbackPoints = [
    ...(pkg.is_dynamic
      ? [`Choose ${pkg.library_selection_count} of ${libraries.length} available libraries`]
      : libraries.map((l) => `Full ${l.name} Access`)),
    "All Video Categories",
    "Stream on Any Device",
    `${pkg.duration_days} Days Validity`,
  ];

  const features = pkg.description_points?.length
    ? pkg.description_points
    : fallbackPoints;

  return (
    <div
      className={`pricingtable-wrapper box-hover style-1 hpa-plan-card ${active ? "active" : ""}`}
      onMouseEnter={onMouseEnter}
    >
      <div className="pricingtable-inner">

        {/* Package name tab — absolute top label */}
        <div className="pricingtable-title">{pkg.name}</div>

        {/* Price */}
        <div className="pricingtable-price">
          <h2 className="pricingtable-bx text-primary">
            {pkg.currency} {pkg.price}
            <small>{durationLabel(pkg.duration_days, t)}</small>
          </h2>
        </div>

        {/* ── Accessible libraries ── */}
        <div style={libSection}>
          <p style={libLabel}>
            {pkg.is_dynamic
              ? `Choose ${pkg.library_selection_count} from`
              : t("Gives Access To")}
          </p>
          {libraries.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {libraries.map((lib) => (
                <button
                  key={lib.id}
                  style={libTagBase}
                  onClick={() => navigate(`/library/${lib.slug}`)}
                  title={`Browse ${lib.name}`}
                >
                  {lib.name}
                </button>
              ))}
            </div>
          ) : (
            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>—</span>
          )}
        </div>

        {/* Features */}
        <ul className="pricingtable-features">
          {features.map((point, i) => (
            <li key={i}>{t(point)}</li>
          ))}
        </ul>

        {/* CTA */}
        <div className="pricingtable-footer">
          <button className="btn btn-primary btn-skew" onClick={onGetAccess}>
            <span>{t("Get Access")}</span>
          </button>
        </div>

      </div>
    </div>
  );
};

// ─── PricingTable ─────────────────────────────────────────────────────────────

const PricingTable = ({ showBackground = false, backgroundImage = null }) => {
  const { t, language } = useLanguage();
  const [packages, setPackages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [dynamicPackage, setDynamicPackage] = useState(null);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const { user } = useAuth();
  const navigate  = useNavigate();
  const prevRef   = useRef(null);
  const nextRef   = useRef(null);

  useEffect(() => {
    apiClient.get("/packages")
      .then((res) => setPackages(res.data.data || []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [language]);

  const initiatePayment = async (pkg, selectedIds = []) => {
    setPaymentLoading(true);
    setPaymentError("");

    try {
      const { data } = await apiClient.post("/payments/initiate", {
        package_id: pkg.id,
        selected_library_ids: selectedIds,
      });
      const collectUrl = data.data?.collect_url;

      if (!collectUrl) {
        throw new Error("The payment provider did not return a payment link.");
      }

      window.location.href = collectUrl;
    } catch (error) {
      setPaymentError(
        error.response?.data?.message
          || Object.values(error.response?.data?.errors || {})?.[0]?.[0]
          || error.message
          || "Could not prepare payment. Please try again.",
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleGetAccess = (pkg) => {
    if (!user) {
      navigate("/auth");
      return;
    }

    if (pkg.is_dynamic) {
      setSelectedLibraryIds([]);
      setPaymentError("");
      setDynamicPackage(pkg);
      return;
    }

    initiatePayment(pkg);
  };

  const toggleDynamicLibrary = (libraryId) => {
    setSelectedLibraryIds((current) => (
      current.includes(libraryId)
        ? current.filter((id) => id !== libraryId)
        : [...current, libraryId]
    ));
  };

  if (loading) {
    return (
      <section className="content-inner rounded-shape-top overflow-hidden bg-white">
        <div className="container text-center py-5">
          <p>{t("Loading plans…")}</p>
        </div>
      </section>
    );
  }

  if (!packages.length) return null;

  const useSlider = packages.length > 3;

  return (
    <section
      className={`content-inner rounded-shape-top overflow-hidden hpa-pricing-section ${showBackground ? "" : "bg-white"}`}
      style={showBackground && backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className="container">

        {useSlider ? (
          <div style={{ position: "relative" }}>
            <button
              ref={prevRef}
              className="btn btn-primary btn-skew"
              style={{
                position: "absolute", left: "-20px", top: "50%",
                transform: "translateY(-50%)", zIndex: 10,
                padding: "8px 14px", lineHeight: 1,
              }}
            >
              <i className="fa fa-chevron-left" />
            </button>
            <button
              ref={nextRef}
              className="btn btn-primary btn-skew"
              style={{
                position: "absolute", right: "-20px", top: "50%",
                transform: "translateY(-50%)", zIndex: 10,
                padding: "8px 14px", lineHeight: 1,
              }}
            >
              <i className="fa fa-chevron-right" />
            </button>

            <Swiper
              modules={[Navigation]}
              slidesPerView={3}
              spaceBetween={24}
              navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
              onBeforeInit={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
              }}
              onSlideChange={(swiper) => setActiveIdx(swiper.activeIndex)}
              breakpoints={{
                0:   { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                992: { slidesPerView: 3 },
              }}
            >
              {packages.map((pkg, i) => (
                <SwiperSlide key={pkg.id}>
                  <PricingCard
                    pkg={pkg}
                    active={i === activeIdx}
                    onMouseEnter={() => setActiveIdx(i)}
                    onGetAccess={() => handleGetAccess(pkg)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : (
          <div className="row">
            {packages.map((pkg, i) => (
              <div className="col-lg-4 col-md-6 m-b30" key={pkg.id}>
                <PricingCard
                  pkg={pkg}
                  active={i === activeIdx}
                  onMouseEnter={() => setActiveIdx(i)}
                  onGetAccess={() => handleGetAccess(pkg)}
                />
              </div>
            ))}
          </div>
        )}

      </div>
      <DynamicLibraryModal
        pkg={dynamicPackage}
        selectedIds={selectedLibraryIds}
        onToggle={toggleDynamicLibrary}
        onClose={() => {
          if (!paymentLoading) setDynamicPackage(null);
        }}
        onConfirm={() => initiatePayment(dynamicPackage, selectedLibraryIds)}
        loading={paymentLoading}
        error={paymentError}
      />
    </section>
  );
};

export default PricingTable;
